/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Radio, Select, VFlow } from 'bold-ui'
import { PivotTableProps } from 'components/pivot-table/PivotTable'
import { Aggregator } from 'components/pivot-table/types'
import React, { useState } from 'react'

export type AggregatorsProps<T extends any> = {
  sample: T
  keyMapping: PivotTableProps<T>['keyMapping']
  handleAggregatorChange: (aggregator: Aggregator) => void
  handleAggregatorKeyChange: (key: keyof T) => void
}

export function Aggregators<T extends any>(props: AggregatorsProps<T>) {
  const { sample, keyMapping, handleAggregatorKeyChange, handleAggregatorChange } = props

  const [aggregator, setAggregator] = useState<Aggregator>(functionArr[0])
  const [key, setKey] = useState<keyof T>()

  const numberKeys: any[] = Object.keys(sample).filter((k) => typeof sample[k] === 'number')

  const itemToString = (item: keyof T | null) => (item ? keyMapping.get(item).keyName || (item as string) : '')

  const handleKeySelect = (item: keyof T) => {
    setKey(item)
    handleAggregatorKeyChange(item)
  }

  const handleAggregatorSelect = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const idx = Number(evt.target.value)
    const agg = functionArr[idx]
    setAggregator(agg)
    handleAggregatorChange(agg)
  }

  return (
    <VFlow>
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-start;
        `}
      >
        {functionArr
          .filter((f) => (numberKeys.length === 0 ? !f.keyDependent : true))
          .map((f, idx) => (
            <div
              key={f.id}
              css={css`
                margin-bottom: 0.5rem;
                margin-right: 1rem;
              `}
            >
              <Radio
                name='aggregator'
                checked={aggregator.id === f.id}
                disabled={f.keyDependent && numberKeys.length === 0}
                label={f.label}
                value={idx}
                onChange={handleAggregatorSelect}
              />
            </div>
          ))}
      </div>
      {!['contagem', 'percentual'].includes(aggregator?.id) && (
        <Select<keyof T>
          disabled={numberKeys.length === 0}
          items={numberKeys}
          itemToString={itemToString}
          value={key}
          onChange={handleKeySelect}
        />
      )}
    </VFlow>
  )
}

const contagem: Aggregator = { id: 'contagem', label: 'Contagem', value: undefined, keyDependent: false }

const functionArr: Aggregator[] = [
  contagem,
  {
    id: 'percentual',
    label: 'Percentual',
    value: (values: number[], total: number): number => (values.reduce((prev, curr) => prev + curr, 0) * 100) / total,
    keyDependent: false,
    chain: [contagem],
    suffix: '%',
  },
  {
    id: 'media',
    label: 'Média',
    value: (values: number[]): number => values.reduce((prev, curr) => prev + curr, 0) / values.length,
    keyDependent: true,
  },
  {
    id: 'maximo',
    label: 'Máximo',
    value: (values: number[]): number => values.reduce((prev, curr) => (prev > curr ? prev : curr)),
    keyDependent: true,
  },
  {
    id: 'minimo',
    label: 'Mínimo',
    value: (values: number[]): number => values.reduce((prev, curr) => (prev < curr ? prev : curr)),
    keyDependent: true,
  },
]
